import React, { useState } from "react";
import { api } from "../lib/api";
import { ExternalLink, ShieldCheck, Copy, Check } from "lucide-react";

/** BIS Hallmark certificate viewer — shows our catalogue record + points to the official BIS Care app for HUID verification. */
export default function HallmarkViewer({ huid, compact = false }) {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const lookup = async () => {
    if (!huid) return;
    try {
      const { data } = await api.get(`/api/hallmark/verify/${encodeURIComponent(huid)}`);
      setInfo(data);
    } catch {}
  };
  React.useEffect(() => {
    if (huid && !compact) lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huid]);

  const copyHuid = async () => {
    try {
      await navigator.clipboard.writeText(huid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  if (!huid) return null;

  if (compact) {
    return (
      <a href="https://www.bis.gov.in/bis-apps/?lang=en" target="_blank" rel="noreferrer" className="text-xs text-maroon underline inline-flex items-center gap-1" data-testid="bis-link">
        <ShieldCheck size={12}/> Verify HUID {huid} on BIS Care
      </a>
    );
  }

  return (
    <div className="bg-white border border-line p-4" data-testid="hallmark-viewer">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">BIS Hallmark Certificate</div>
          <div className="text-lg font-semibold tracking-tight text-ink mt-1">HUID {huid}</div>
        </div>
        <ShieldCheck className="text-gold" size={28}/>
      </div>

      {info && (
        <div className="mt-3 text-sm space-y-1">
          <div>Stored in our catalogue: <span className="font-medium">{info.found_in_store ? "Yes" : "No"}</span></div>
          {info.product && (
            <>
              <div>Product: <span className="font-medium">{info.product.name}</span></div>
              <div className="text-xs text-ash">Purity {info.product.purity} · {info.product.weight_grams}g · {info.product.hallmark}</div>
            </>
          )}
        </div>
      )}

      <div className="mt-4 rounded-lg bg-pearl border border-line p-3 text-xs text-ash leading-relaxed">
        To independently verify this piece, open the official <span className="font-medium text-ink">BIS Care</span> app and choose <span className="font-medium text-ink">“Verify HUID”</span>, then enter the 6-digit HUID above. The app is the government's official channel for hallmark verification.
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={copyHuid} className="btn-outline text-xs" data-testid="bis-copy-huid">
          {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy HUID</>}
        </button>
        <a href="https://play.google.com/store/apps/details?id=com.bis.bisapp" target="_blank" rel="noreferrer" className="btn-outline text-xs" data-testid="bis-android">
          <ExternalLink size={12}/> BIS Care (Android)
        </a>
        <a href="https://apps.apple.com/in/app/bis-care-app/id6443724891" target="_blank" rel="noreferrer" className="btn-outline text-xs" data-testid="bis-ios">
          <ExternalLink size={12}/> BIS Care (iOS)
        </a>
      </div>
    </div>
  );
}